import { useMemo } from 'react';
import TransactionItem from './Item';
import CustomTransactionItem from './Item/Custom';
import './index.css';
import ComplexParamsBundleComponent from './Item/ComplexParamsBundle';
import LegacyIndicator from './Item/LegacyIndicator';
import LatestBotStateComponent from './Item/LatestBotState';
import { ComplexParamsBundle, LatestBotState } from '../../../../Detail/types';
import {
  CbrtRangeState,
  IndicatorConfig,
  MrcState,
  RSIItem,
  WakeupTimes,
} from '../../../../ProfitCalculations/types';
import { compareComplexParamsBundle } from '../../../../../helpers/bundleTransactionsToCompactForm';
import { GenericTransactionInfo } from '../../../../../types/Transaction';
import { CustomTransaction } from 'grts-utils/types/Transaction';

interface Props {
  transactions: Array<GenericTransactionInfo | CustomTransaction> | undefined;
  isLoading: boolean;
  compactTransactionLength?: number | undefined;
  currentComplexParams?: ComplexParamsBundle;
  transactionsByTimestamp?: Record<string, GenericTransactionInfo[]>;
  cbrtRangeStates?: CbrtRangeState[];
  mrcStates?: MrcState[] | undefined;
  wakeupTimes?: WakeupTimes;
  indicatorConfig?: IndicatorConfig;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  transactionTypeToggleValues?: {
    core: boolean;
    takeProfit: boolean;
    stopLoss: boolean;
  };
  useTakeProfitTransactions: boolean;
  rsiIndicatorValuesOppositeOrder?: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder?: RSIItem[] | undefined;
  latestBotState: LatestBotState | undefined;
}

const isCustomTransaction = (
  transaction: GenericTransactionInfo | CustomTransaction,
): transaction is CustomTransaction =>
  transaction.transactionType.indexOf('_BALANCE') !== -1;

// const isTransaction = (
//   item: DetailCompactTransaction | ComplexParamsBundle | CustomTransaction,
// ): item is DetailCompactTransaction => item.hasOwnProperty('transactionType');

const isBotLatestState = (
  item:
    | GenericTransactionInfo
    | ComplexParamsBundle
    | LatestBotState
    | CustomTransaction,
): item is LatestBotState => item.hasOwnProperty('cbrtRangeStates');

const isComplexParamsBundle = (
  item:
    | GenericTransactionInfo
    | ComplexParamsBundle
    | LatestBotState
    | CustomTransaction,
): item is ComplexParamsBundle =>
  (Object.keys(item).length === 2 || Object.keys(item).length === 3) &&
  item.hasOwnProperty('complexParams') &&
  item.hasOwnProperty('timeFrames');

const TransactionList: React.FC<Props> = (props: Props) => {
  const {
    transactions,
    compactTransactionLength,
    currentComplexParams,
    cbrtRangeStates,
    mrcStates,
    wakeupTimes,
    indicatorConfig,
    currentProfit,
    currentPriceChange,
    transactionTypeToggleValues,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
    latestBotState,
  } = props;

  const items = useMemo(() => {
    const compactTransactions = transactions;
    // const takeProfitTransactions = []
    // transactions?.allTransactions.filter((t) =>
    //   t.transactionType.includes('TAKE_PROFIT'),
    // ) || [];

    if (!compactTransactions?.length) {
      return [];
    }

    const items: Array<
      | string
      | ComplexParamsBundle
      | LatestBotState
      | GenericTransactionInfo
      | CustomTransaction
      | GenericTransactionInfo[]
    > = [];

    if (currentComplexParams) {
      items.push(currentComplexParams);
    }

    /** Push latest state */
    if (latestBotState) {
      items.push(latestBotState);
    }

    let complexParamsBundle = currentComplexParams;
    let isLegacyPushed = false;

    // if (isDoubleView && transactionsByTimestamp) {
    //   /** Use transactionsByTimestamp */
    //   const entries = Object.entries(transactionsByTimestamp).sort(
    //     (a, b) => parseFloat(b[0]) - parseFloat(a[0]),
    //   );
    //   data = entries.map((e) => e[1]);
    // } else {
    const data = [...compactTransactions]
      .sort((a, b) => b.transactionIndex - a.transactionIndex)
      .filter((transaction) => {
        if (!transactionTypeToggleValues) return true;

        if (
          transaction.transactionType.includes('TAKE_PROFIT') &&
          transaction.transactionType.includes('STOP_LOSS') &&
          transactionTypeToggleValues.stopLoss
        ) {
          return true;
        }

        if (
          transaction.transactionType.includes('TAKE_PROFIT') &&
          !transaction.transactionType.includes('STOP_LOSS') &&
          transactionTypeToggleValues.takeProfit
        ) {
          return true;
        }

        if (
          !transaction.transactionType.includes('TAKE_PROFIT') &&
          !transaction.transactionType.includes('STOP_LOSS') &&
          transactionTypeToggleValues.core
        ) {
          return true;
        }
      });

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      let transaction: GenericTransactionInfo | CustomTransaction;

      if (Array.isArray(datum)) {
        transaction = datum[0];
      } else {
        transaction = datum;
      }

      if (
        !complexParamsBundle &&
        !isCustomTransaction(transaction) &&
        !!transaction.complexParams &&
        !!transaction.timeFrames
      ) {
        complexParamsBundle = {
          complexParams: transaction.complexParams,
          timeFrames: transaction.timeFrames,
          indicatorConfig: indicatorConfig,
        };

        items.push(complexParamsBundle);
        items.push(datum);
        continue;
      }

      if (!complexParamsBundle) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      /** If compact transaction has different complex params bundle
       * add that param bundle first
       */

      if (
        !isCustomTransaction(transaction) &&
        (!transaction.complexParams || !transaction.timeFrames)
      ) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      const hasSameComplexParamsBundle = compareComplexParamsBundle(
        complexParamsBundle,
        transaction as GenericTransactionInfo,
      );

      if (hasSameComplexParamsBundle) {
        items.push(datum);
        continue;
      }

      if (
        !isCustomTransaction(transaction) &&
        transaction.complexParams &&
        transaction.timeFrames
      ) {
        complexParamsBundle = {
          complexParams: transaction.complexParams,
          timeFrames: transaction.timeFrames,
        };

        items.push(complexParamsBundle);
      }

      items.push(datum);
    }

    return items;
  }, [
    currentComplexParams,
    indicatorConfig,
    latestBotState,
    transactionTypeToggleValues,
    transactions,
  ]);

  const compactTransactionWithoutCustomTransactions = useMemo(() => {
    return transactions
      ? (([...transactions]
          ?.filter((t) => !isCustomTransaction(t))
          .sort(
            (a, b) =>
              (a as GenericTransactionInfo).transactionIndex -
              (b as GenericTransactionInfo).transactionIndex,
          ) ?? []) as GenericTransactionInfo[])
      : [];
  }, [transactions]);

  const compactTransactionWithoutCustomTransactionsAndTPTs = useMemo(() => {
    return transactions
      ? (([...transactions]
          ?.filter((t) => !isCustomTransaction(t))
          ?.filter((t) => !t.transactionType.includes('TAKE_PROFIT'))
          .sort(
            (a, b) =>
              (a as GenericTransactionInfo).transactionIndex -
              (b as GenericTransactionInfo).transactionIndex,
          ) ?? []) as GenericTransactionInfo[])
      : [];
  }, [transactions]);

  const firstTransactionItemIndex = items.findIndex((item) => {
    if (
      typeof item === 'string' ||
      isBotLatestState(Array.isArray(item) ? item[0] : item) ||
      isComplexParamsBundle(Array.isArray(item) ? item[0] : item) ||
      isCustomTransaction((Array.isArray(item) ? item[0] : item) as any)
    ) {
      return false;
    }

    return true;
  });

  const renderTransactionItem = (
    item:
      | GenericTransactionInfo
      | CustomTransaction
      | GenericTransactionInfo[]
      | ComplexParamsBundle
      | LatestBotState
      | string,
    index: number,
  ) => {
    if (!transactions) {
      return <></>;
    }

    if (typeof item === 'string') {
      return <LegacyIndicator />;
    }

    const transaction = Array.isArray(item) ? item[0] : item;

    if (isBotLatestState(transaction)) {
      return (
        <LatestBotStateComponent
          latestBotState={transaction}
          complexParamsChangeTimestamps={[]}
        />
      );
    }

    if (isComplexParamsBundle(transaction)) {
      return (
        <ComplexParamsBundleComponent
          complexParamsBundle={transaction}
          isFirst={index === 0}
        />
      );
    }

    if (isCustomTransaction(transaction)) {
      return (
        <CustomTransactionItem
          key={index}
          transaction={transaction}
          index={index}
          totalLength={items?.length}
        />
      );
    }

    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      const startTimestamp =
        transaction.candles?.multi?.[0]?.startTimestamp ?? 0;
      const endTimestamp =
        transaction.candles?.higher?.[transaction.candles?.higher?.length - 1]
          ?.endTimestamp ?? 0;

      const cpt = compactTransactionWithoutCustomTransactions
        .filter(
          (t) =>
            startTimestamp <= t.transactionTimestamp &&
            t.transactionTimestamp <= endTimestamp,
        )
        .filter(
          (t) =>
            transaction.transactionIndex === t.transactionIndex ||
            transaction.transactionIndex === t.originalId ||
            t.transactionIndex === transaction.originalId ||
            (!t.transactionType.includes('STOP_LOSS') &&
              !t.transactionType.includes('TAKE_PROFIT')),
        );

      cpt.push(transaction);

      const oppositeOrderRsiValues = rsiIndicatorValuesOppositeOrder?.filter(
        (s) =>
          s.timestamp >= (startTimestamp ?? 0) &&
          s.timestamp <= (endTimestamp ?? 0),
      );
      const sameOrderRsiValues = rsiIndicatorValuesSameOrder?.filter(
        (s) =>
          s.timestamp >= (startTimestamp ?? 0) &&
          s.timestamp <= (endTimestamp ?? 0),
      );

      return (
        <TransactionItem
          key={index}
          index={index}
          transaction={transaction}
          compactTransactionIndex={null}
          currentProfit={
            index === firstTransactionItemIndex ? currentProfit : undefined
          }
          currentPriceChange={
            index === firstTransactionItemIndex ? currentPriceChange : undefined
          }
          compactTransactionsLength={0}
          clusteredTransactions={[transaction]}
          compactTransactions={cpt}
          showCandles={true}
          cbrtRangeStates={cbrtRangeStates?.filter((s) =>
            transaction?.candles && transaction?.candles?.higher?.length > 0
              ? transaction.candles.higher[0].startTimestamp <= s.timestamp &&
                s.timestamp <=
                  transaction.candles.higher[
                    transaction.candles.higher.length - 1
                  ].endTimestamp
              : 0,
          )}
          mrcStates={mrcStates?.filter((s) =>
            transaction?.candles && transaction?.candles?.higher?.length > 0
              ? transaction.candles.higher[0].startTimestamp <=
                  s.startTimestamp &&
                s.startTimestamp <=
                  transaction.candles.higher[
                    transaction.candles.higher.length - 1
                  ].endTimestamp
              : 0,
          )}
          timeFrames={currentComplexParams?.timeFrames}
          showCurrentProfit={true}
          wakeupTimes={wakeupTimes}
          indicatorConfig={indicatorConfig}
          rsiIndicatorValuesOppositeOrder={oppositeOrderRsiValues}
          rsiIndicatorValuesSameOrder={sameOrderRsiValues}
        />
      );
    }

    const compactTransaction = transaction as GenericTransactionInfo;

    const compactTransactionId = compactTransaction.compactTransactionId;
    const compactTransactionList =
      compactTransactionWithoutCustomTransactionsAndTPTs
        ? [...compactTransactionWithoutCustomTransactionsAndTPTs].sort(
            (a, b) => b.transactionIndex - a.transactionIndex,
          )
        : [];

    const rootIndex =
      compactTransactionList.findIndex(
        (t) =>
          !!t.compactTransactionId &&
          t.compactTransactionId === compactTransactionId,
      ) ?? 0;

    const compactTransactions: Array<GenericTransactionInfo> = [];

    if (rootIndex > 0) {
      compactTransactions.push(compactTransactionList[rootIndex - 1]);
    }

    compactTransactions.push(compactTransactionList[Math.max(0, rootIndex)]);

    compactTransactions.push(
      compactTransactionList[
        Math.min(
          rootIndex + 1,
          compactTransactionWithoutCustomTransactionsAndTPTs?.length
            ? compactTransactionWithoutCustomTransactionsAndTPTs.length - 1
            : 0,
        )
      ],
    );

    const takeProfitTransactions =
      compactTransactionWithoutCustomTransactions.filter(
        (t) =>
          compactTransactions[Math.max(0, compactTransactions.length - 1)]
            .transactionTimestamp <= t.transactionTimestamp &&
          t.transactionTimestamp <=
            (rootIndex === 0
              ? Date.now()
              : compactTransactions[0].transactionTimestamp),
      );

    compactTransactions.push(...takeProfitTransactions);

    const clusteredTransactions = [
      ...compactTransactionWithoutCustomTransactionsAndTPTs,
    ]
      .filter(
        (t) =>
          t.compactTransactionId === compactTransaction.compactTransactionId,
      )
      .sort((a, b) => b.transactionIndex - a.transactionIndex);

    const startTimestamp = compactTransaction.candles?.higher[0].startTimestamp;
    const endTimestamp =
      compactTransaction.candles?.higher[
        compactTransaction.candles?.higher.length - 1
      ].startTimestamp;

    const oppositeOrderRsiValues = rsiIndicatorValuesOppositeOrder?.filter(
      (s) =>
        s.timestamp >= (startTimestamp ?? 0) &&
        s.timestamp <= (endTimestamp ?? 0),
    );
    const sameOrderRsiValues = rsiIndicatorValuesSameOrder?.filter(
      (s) =>
        s.timestamp >= (startTimestamp ?? 0) &&
        s.timestamp <= (endTimestamp ?? 0),
    );

    return (
      <TransactionItem
        key={index}
        index={index}
        transaction={compactTransaction}
        compactTransactionIndex={
          compactTransactionWithoutCustomTransactionsAndTPTs?.findIndex(
            (t) =>
              t.compactTransactionId ===
              compactTransaction.compactTransactionId,
          ) ?? 0
        }
        currentProfit={
          index === firstTransactionItemIndex ? currentProfit : undefined
        }
        currentPriceChange={
          index === firstTransactionItemIndex ? currentPriceChange : undefined
        }
        compactTransactionsLength={compactTransactionLength || 0}
        clusteredTransactions={clusteredTransactions}
        compactTransactions={
          compactTransactions as unknown as GenericTransactionInfo[]
        }
        showCandles={true}
        cbrtRangeStates={cbrtRangeStates?.filter((s) =>
          compactTransaction?.candles &&
          compactTransaction?.candles?.higher?.length > 0
            ? compactTransaction.candles.higher[0].startTimestamp <=
                s.timestamp &&
              s.timestamp <=
                compactTransaction.candles.higher[
                  compactTransaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        mrcStates={mrcStates?.filter((s) =>
          compactTransaction?.candles &&
          compactTransaction?.candles?.higher?.length > 0
            ? compactTransaction.candles.higher[0].startTimestamp <=
                s.startTimestamp &&
              s.startTimestamp <=
                compactTransaction.candles.higher[
                  compactTransaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        timeFrames={currentComplexParams?.timeFrames}
        showCurrentProfit={
          compactTransactionWithoutCustomTransactionsAndTPTs?.findIndex(
            (t) =>
              t.compactTransactionId ===
              compactTransaction.compactTransactionId,
          ) ===
          compactTransactionWithoutCustomTransactionsAndTPTs.length - 1
        }
        wakeupTimes={wakeupTimes}
        indicatorConfig={indicatorConfig}
        rsiIndicatorValuesOppositeOrder={oppositeOrderRsiValues}
        rsiIndicatorValuesSameOrder={sameOrderRsiValues}
      />
    );
  };

  if (!transactions) {
    return (
      <span className="detail-transactions-no-transactions-found">
        No transactions found
      </span>
    );
  }

  return (
    <div className="detail-transactions-container">
      {items.map(renderTransactionItem)}
    </div>
  );
};

export default TransactionList;
